/**
 * AgentControlBar - Official LiveKit Agents UI component (Hybrid/RoomContext).
 * Use with LiveKitRoom (connect={false}) for manually managed rooms.
 * Full controls: microphone, camera, screen share, chat, leave.
 *
 * @see https://docs.livekit.io/reference/components/agents-ui/component/agent-control-bar/
 */
import { useEffect, useRef, useState } from 'react';
import { useChat } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { Loader, MessageSquare, SendHorizontal } from 'lucide-react';
import { motion } from 'motion/react';
import { cn } from '../../lib/utils';
import { useCandidateT } from '../../i18n/candidate';
import { AgentDisconnectButton } from './agent-disconnect-button';
import { AgentTrackControl } from '../ui/agent-track-control';
import { AgentTrackToggle, agentTrackToggleVariants } from '../ui/agent-track-toggle';
import { Button } from '../ui/ui/button';
import { Toggle } from '../ui/ui/toggle';
import {
  useInputControls,
  usePublishPermissions,
} from '../../hooks/agents-ui/use-agent-control-bar';

const LK_TOGGLE_VARIANT_1 = [
  'data-[state=off]:bg-accent data-[state=off]:hover:bg-foreground/10',
  'data-[state=off]:[&_~_button]:bg-accent data-[state=off]:[&_~_button]:hover:bg-foreground/10',
  'data-[state=off]:border-border data-[state=off]:hover:border-foreground/12',
  'data-[state=off]:[&_~_button]:border-border data-[state=off]:[&_~_button]:hover:border-foreground/12',
  'data-[state=off]:text-destructive data-[state=off]:hover:text-destructive data-[state=off]:focus:text-destructive',
  'data-[state=off]:focus-visible:ring-foreground/12 data-[state=off]:focus-visible:border-ring',
  'dark:data-[state=off]:[&_~_button]:bg-accent dark:data-[state=off]:[&_~_button]:hover:bg-foreground/10',
];

const LK_TOGGLE_VARIANT_2 = [
  'data-[state=off]:bg-accent data-[state=off]:hover:bg-foreground/10',
  'data-[state=off]:border-border data-[state=off]:hover:border-foreground/12',
  'data-[state=off]:focus-visible:border-ring data-[state=off]:focus-visible:ring-foreground/12',
  'data-[state=off]:text-foreground data-[state=off]:hover:text-foreground data-[state=off]:focus:text-foreground',
  'data-[state=on]:bg-blue-500/20 data-[state=on]:hover:bg-blue-500/30',
  'data-[state=on]:border-blue-700/10 data-[state=on]:text-blue-700 data-[state=on]:ring-blue-700/30',
  'data-[state=on]:focus-visible:border-blue-700/50',
  'dark:data-[state=on]:bg-blue-500/20 dark:data-[state=on]:text-blue-300',
];

const MOTION_PROPS = {
  variants: {
    hidden: { height: 0, opacity: 0, marginBottom: 0 },
    visible: { height: 'auto', opacity: 1, marginBottom: 12 },
  },
  initial: 'hidden',
  transition: { duration: 0.3, ease: 'easeOut' },
};

function AgentChatInput({ chatOpen, onSend = async () => {}, className }) {
  const inputRef = useRef(null);
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState('');
  const t = useCandidateT();
  const isDisabled = isSending || message.trim().length === 0;

  const handleSend = async () => {
    if (isDisabled) return;
    try {
      setIsSending(true);
      await onSend(message.trim());
      setMessage('');
    } catch (error) {
      console.error(error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = async (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  useEffect(() => {
    if (chatOpen) return;
    inputRef.current?.focus();
  }, [chatOpen]);

  return (
    <div className={cn('mb-3 flex grow items-end gap-2 rounded-md pl-1 text-sm', className)}>
      <textarea
        autoFocus
        ref={inputRef}
        value={message}
        disabled={!chatOpen || isSending}
        placeholder={t('Type something...')}
        onKeyDown={handleKeyDown}
        onChange={(e) => setMessage(e.target.value)}
        className="field-sizing-content max-h-16 min-h-8 flex-1 resize-none py-2 [scrollbar-width:thin] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
      />
      <Button
        size="icon"
        type="button"
        disabled={isDisabled}
        variant={isDisabled ? 'secondary' : 'default'}
        title={isSending ? t('Sending...') : t('Send')}
        onClick={() => handleSend()}
        className="self-end disabled:cursor-not-allowed"
      >
        {isSending ? <Loader className="animate-spin" /> : <SendHorizontal />}
      </Button>
    </div>
  );
}

export function AgentControlBar({
  variant = 'default',
  controls,
  isChatOpen = false,
  isConnected = false,
  saveUserChoices = true,
  onDisconnect,
  onDeviceError,
  onIsChatOpenChange,
  elapsedTime,
  formatTime,
  cameraDisabled = false,
  hideChatInput = false,
  className,
  ...props
}) {
  const { send } = useChat();
  const publishPermissions = usePublishPermissions();
  const [isChatOpenUncontrolled, setIsChatOpenUncontrolled] = useState(isChatOpen);
  const {
    microphoneTrack,
    cameraToggle,
    microphoneToggle,
    screenShareToggle,
    handleAudioDeviceChange,
    handleVideoDeviceChange,
    handleMicrophoneDeviceSelectError,
    handleCameraDeviceSelectError,
  } = useInputControls({ onDeviceError, saveUserChoices });
  const t = useCandidateT();

  const handleSendMessage = async (message) => {
    await send(message);
  };

  const visibleControls = {
    leave: controls?.leave ?? true,
    microphone: controls?.microphone ?? publishPermissions.microphone,
    screenShare: controls?.screenShare ?? false,
    camera: controls?.camera ?? publishPermissions.camera,
    chat: controls?.chat ?? publishPermissions.data,
  };

  const isEmpty = Object.values(visibleControls).every((value) => !value);
  if (isEmpty) {
    console.warn('AgentControlBar: `visibleControls` contains only false values.');
    return null;
  }

  // Visual state based on connection - but component always renders
  const connectionVisualState = isConnected ? 'connected' : 'connecting';
  const ariaLabel = isConnected
    ? t('Voice assistant controls')
    : t('Voice assistant controls (connecting...)');

  // Screen reader announcement for connection state changes
  const connectionStatusText = isConnected ? t('Connected') : t('Connecting');

  return (
    <div
      aria-label={ariaLabel}
      aria-live="polite"
      aria-atomic="true"
      data-connection-state={connectionVisualState}
      role="toolbar"
      className={cn(
        'bg-background border-input/50 dark:border-muted flex flex-col border p-2 drop-shadow-md/3 md:p-3',
        variant === 'livekit' ? 'rounded-2xl md:rounded-[31px]' : 'rounded-lg',
        // Subtle visual indication when connecting (but still fully visible)
        !isConnected && 'opacity-90',
        className
      )}
      {...props}
    >
      {/* Screen reader only: Announce connection status */}
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {connectionStatusText}
      </span>
      {!hideChatInput && (
        <motion.div
          {...MOTION_PROPS}
          inert={!(isChatOpen || isChatOpenUncontrolled)}
          animate={isChatOpen || isChatOpenUncontrolled ? 'visible' : 'hidden'}
          className="border-input/50 flex w-full items-start overflow-hidden border-b"
        >
          <AgentChatInput
            chatOpen={isChatOpen || isChatOpenUncontrolled}
            onSend={handleSendMessage}
            className={cn(variant === 'livekit' && '[&_button]:rounded-full')}
          />
        </motion.div>
      )}
      <div className="flex gap-0.5 md:gap-1 items-center min-h-0">
        {typeof elapsedTime === 'number' && formatTime && (
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-muted/50 border border-border/50 md:gap-2 md:px-3 md:py-1.5 flex-shrink-0">
            <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-red-500 flex-shrink-0" />
            <span className="text-xs font-mono text-foreground font-medium tabular-nums md:text-sm">
              {formatTime(elapsedTime)}
            </span>
          </div>
        )}
        <div className="flex grow gap-0.5 md:gap-1 min-w-0 [&_button]:h-8 [&_button]:min-w-8 md:[&_button]:h-9 md:[&_button]:min-w-9">
          {visibleControls.microphone && (
            <AgentTrackControl
              variant={variant === 'outline' ? 'outline' : 'default'}
              kind="audioinput"
              aria-label={t('Toggle microphone')}
              source={Track.Source.Microphone}
              pressed={microphoneToggle.enabled}
              disabled={microphoneToggle.pending}
              audioTrack={microphoneTrack}
              onPressedChange={microphoneToggle.toggle}
              onActiveDeviceChange={handleAudioDeviceChange}
              onMediaDeviceError={handleMicrophoneDeviceSelectError}
              className={cn(
                variant === 'livekit' && [
                  LK_TOGGLE_VARIANT_1,
                  'rounded-full [&_button:first-child]:rounded-l-full [&_button:last-child]:rounded-r-full',
                ]
              )}
            />
          )}
          {/* commented camera code for now as we are not using camera */}
          {/* {visibleControls.camera && (
            <AgentTrackControl
              variant={variant === 'outline' ? 'outline' : 'default'}
              kind="videoinput"
              aria-label="Toggle camera"
              source={Track.Source.Camera}
              pressed={cameraToggle.enabled}
              pending={cameraToggle.pending}
              disabled={cameraDisabled || cameraToggle.pending}
              onPressedChange={cameraToggle.toggle}
              onMediaDeviceError={handleCameraDeviceSelectError}
              onActiveDeviceChange={handleVideoDeviceChange}
              className={cn(
                variant === 'livekit' && [
                  LK_TOGGLE_VARIANT_1,
                  'rounded-full [&_button:first-child]:rounded-l-full [&_button:last-child]:rounded-r-full',
                ]
              )}
            />
          )} */}
          {visibleControls.screenShare && (
            <AgentTrackToggle
              variant={variant === 'outline' ? 'outline' : 'default'}
              aria-label={t('Toggle screen share')}
              source={Track.Source.ScreenShare}
              pressed={screenShareToggle.enabled}
              disabled={screenShareToggle.pending}
              onPressedChange={screenShareToggle.toggle}
              className={cn(variant === 'livekit' && [LK_TOGGLE_VARIANT_2, 'rounded-full'])}
            />
          )}
          {visibleControls.chat && (
            <Toggle
              variant={variant === 'outline' ? 'outline' : 'default'}
              pressed={isChatOpen || isChatOpenUncontrolled}
              aria-label={t('Toggle transcript')}
              onPressedChange={(state) => {
                if (!onIsChatOpenChange) setIsChatOpenUncontrolled(state);
                else onIsChatOpenChange(state);
              }}
              className={agentTrackToggleVariants({
                variant: variant === 'outline' ? 'outline' : 'default',
                className: cn(variant === 'livekit' && [LK_TOGGLE_VARIANT_2, 'rounded-full']),
              })}
            >
              <MessageSquare />
            </Toggle>
          )}
        </div>
        {visibleControls.leave && (
          <AgentDisconnectButton
            onClick={onDisconnect}
            // Always enabled - user should be able to end call even when connecting/disconnected
            disabled={false}
            aria-label={isConnected ? t('End call') : t('End call (connecting)')}
            className={cn(
              variant === 'livekit' &&
                'bg-destructive/10 dark:bg-destructive/10 text-destructive hover:bg-destructive/20 dark:hover:bg-destructive/20 focus:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/4 rounded-full font-mono text-[11px] md:text-xs font-bold tracking-wider px-3 py-1.5 md:px-4 md:py-2 flex-shrink-0',
              // Visual indication when not connected but still functional
              !isConnected && 'opacity-90'
            )}
          >
            <span className="hidden md:inline">{t('END CALL')}</span>
            <span className="inline md:hidden">{t('END')}</span>
          </AgentDisconnectButton>
        )}
      </div>
    </div>
  );
}
